import React from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Combobox } from '../../common/components/combobox'
import { FirmwareGeneralModel, useFirmwareUpdateFirmwareMutation } from '../../common/redux/api/web-api'
import Input from '../../common/components/Input'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import useText from '../../common/hooks/useText'

export const statusList = ['New', 'UpdateToAvailable', 'UpdateToUnavailable', 'UpdateFromUnavailable']

export type EditFirmwareModalProps = {
    selectedFirmwareData: FirmwareGeneralModel
    setEditFirmwareModalIsOpen: (editDescriptionModal: boolean) => void
}
export function EditFirmwareModal({
    setEditFirmwareModalIsOpen,
    selectedFirmwareData,
}: EditFirmwareModalProps): React.ReactElement {
    const text = useText()
    const [updateFirmWare] = useFirmwareUpdateFirmwareMutation()
    const firmwareUpdate = async (formValues: FirmwareGeneralModel) => {
        const tempFirmWare = {
            ...selectedFirmwareData,
            ...formValues,
        }
        try {
            await updateFirmWare({
                firmwareGeneralModel: tempFirmWare,
            }).unwrap()
            setEditFirmwareModalIsOpen(false)
            notifySuccess(text('lblFirmwareVersionUpdatedSuccess'))
        } catch {
            notifyError(text('lblFirmwareVersionUpdatedFailed'))
        }
    }
    const statusOptions = statusList.map((option) => ({ value: option, label: option }))

    const {
        handleSubmit,
        register,
        control,
        formState: { isSubmitting, isDirty },
    } = useForm<FirmwareGeneralModel>({
        mode: 'all',
        values: {
            description: selectedFirmwareData?.description,
            firmwareStatus: selectedFirmwareData?.firmwareStatus,
        },
    })

    return (
        <BootstrapModalLayout
            title={text('lblEditFirmware')}
            size="lg"
            data-testid="edit-firmware-modal"
            actions={[
                {
                    label: text('btnCancel'),
                    btn: 'btn-secondary',
                    callback: () => setEditFirmwareModalIsOpen(false),
                },
                {
                    label: text('btnSave'),
                    callback: handleSubmit(firmwareUpdate),
                    loading: isSubmitting,
                    disabled: isSubmitting || !isDirty,
                },
            ]}
        >
            <span className="flex flex-column gap-7">
                <Controller
                    name="description"
                    control={control}
                    render={({ field: { value } }) => (
                        <Input
                            value={value}
                            {...register('description')}
                            id="description"
                            labelText={text('lblDescription')}
                            data-testid="description-input"
                            type="text"
                        ></Input>
                    )}
                />

                <span className="h-6rem">
                    <Controller
                        name="firmwareStatus"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Combobox
                                options={statusOptions}
                                labelText={text('lblFirmwareStatus')}
                                id="firmwareStatus"
                                data-testid="firmwareStatus-input"
                                enableDefaultOption={false}
                                value={value}
                                onChange={(option) => {
                                    onChange(option.value)
                                }}
                            />
                        )}
                    />
                </span>
            </span>
        </BootstrapModalLayout>
    )
}
